import type { ReactNode } from 'react';

interface StatWidgetProps {
  label: string;
  value: ReactNode;
  icon?: string;
  theme?: 'neutral' | 'primary' | 'success' | 'danger' | 'warning' | 'info';
  // Borderless variant for KPI cards nested inside another card.
  border?: boolean;
  // Optional click handler — turns the whole card into a button-like surface
  // (cursor-pointer). Backward compatible: when omitted the card is static.
  onClick?: () => void;
  // Active/selected highlight (e.g. the Client Assets KPI card that has its
  // breakdown panel open).
  active?: boolean;
  // Extra class for the value element (e.g. text-primary / text-danger).
  valueClassName?: string;
  // Extra class on the outer card (e.g. the recon excess/shortfall glow + hover).
  cardClassName?: string;
  // Bootstrap tooltip text on the card (title attribute, initialised by the
  // page via useBootstrapTooltip when native BS tooltips are wanted).
  tooltip?: string;
}

// Solid, themed backgrounds (text-bg-* gives a solid fill + readable foreground).
// `neutral` deliberately avoids Bootstrap `secondary` (banned by the no-secondary rule).
const THEME_CLASS: Record<NonNullable<StatWidgetProps['theme']>, string> = {
  neutral: 'text-bg-dark',
  primary: 'text-bg-primary',
  success: 'text-bg-success',
  danger: 'text-bg-danger',
  warning: 'text-bg-warning',
  info: 'text-bg-info',
};

// React equivalent of FinkitWidgetManager stat cards.
export function StatWidget({
  label,
  value,
  icon,
  theme = 'neutral',
  border = true,
  onClick,
  active = false,
  valueClassName,
  cardClassName,
  tooltip,
}: StatWidgetProps) {
  const clickable = Boolean(onClick);
  const cls = [
    'card h-100',
    border ? '' : 'border-0 shadow-none',
    clickable ? 'cursor-pointer' : '',
    active ? 'active' : '',
    cardClassName ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div
      className={cls}
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      title={tooltip}
      data-bs-toggle={tooltip ? 'tooltip' : undefined}
    >
      <div className="card-body d-flex align-items-center justify-content-between">
        <div>
          <div className="text-muted small">{label}</div>
          <div className={`fs-5 fw-semibold${valueClassName ? ` ${valueClassName}` : ''}`}>{value}</div>
        </div>
        {icon && (
          <span className={`rounded-circle d-inline-flex align-items-center justify-content-center fs-5 ${THEME_CLASS[theme]}`} style={{ width: 44, height: 44 }}>
            <i className={icon} aria-hidden="true" />
          </span>
        )}
      </div>
    </div>
  );
}
